/**
 * Comparison View - Compare net worth, assets, liabilities across periods
 * Side-by-side month and year comparison with change indicators
 */
import React, { useMemo } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus, GitCompare } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const ComparisonView = ({
  accounts = { assets: [], liabilities: [] },
  previousPeriodLabel = 'Previous',
  currentPeriodLabel = 'Current',
  formatCurrency: fmtCur,
  formatCurrencyShort: fmtCurShort
}) => {
  const fc = fmtCur || ((v) => `$${(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
  const fcShort = fmtCurShort || ((v) => `$${(v / 1000).toFixed(0)}k`);

  // Build comparison data from assets/liabilities
  const comparisonData = useMemo(() => {
    const assets = accounts.assets || [];
    const liabilities = accounts.liabilities || [];

    const getCurrent = (account) => Number(account.currentValue) || 0;
    const getPrevious = (account) => Number(account.previousValue) || 0;

    const currentAssetTotal = assets.reduce((s, a) => s + getCurrent(a), 0);
    const currentLiabilityTotal = liabilities.reduce((s, a) => s + getCurrent(a), 0);
    const prevAssetTotal = assets.reduce((s, a) => s + getPrevious(a), 0);
    const prevLiabilityTotal = liabilities.reduce((s, a) => s + getPrevious(a), 0);

    const periods = [
      {
        period: previousPeriodLabel,
        assets: prevAssetTotal,
        liabilities: prevLiabilityTotal,
        netWorth: prevAssetTotal - prevLiabilityTotal
      },
      {
        period: currentPeriodLabel,
        assets: currentAssetTotal,
        liabilities: currentLiabilityTotal,
        netWorth: currentAssetTotal - currentLiabilityTotal
      }
    ];

    return { periods, changes: {
      assets: currentAssetTotal - prevAssetTotal,
      liabilities: currentLiabilityTotal - prevLiabilityTotal,
      netWorth: (currentAssetTotal - currentLiabilityTotal) - (prevAssetTotal - prevLiabilityTotal)
    }};
  }, [accounts, currentPeriodLabel, previousPeriodLabel]);

  const { periods, changes } = comparisonData;

  // inline-flex (not flex) so the indicator obeys the cell's text alignment
  const ChangeIndicator = ({ value }) => {
    if (value > 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 text-emerald-700 dark:text-emerald-400 text-xs font-medium font-num">
          <ArrowUpRight className="w-3 h-3 flex-shrink-0" />+{fc(Math.abs(value))}
        </span>
      );
    }
    if (value < 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-red-50 dark:bg-red-950/40 px-1.5 py-0.5 text-red-600 dark:text-red-400 text-xs font-medium font-num">
          <ArrowDownRight className="w-3 h-3 flex-shrink-0" />-{fc(Math.abs(value))}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-gray-400 text-xs font-num">
        <Minus className="w-3 h-3 flex-shrink-0" />No change
      </span>
    );
  };

  return (
    <div data-testid="comparison-view" className="space-y-4">
      {/* Change Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm text-center">
          <p className="micro-label text-gray-400 dark:text-gray-500 mb-1.5">Assets Change</p>
          <ChangeIndicator value={changes.assets} />
        </div>
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm text-center">
          <p className="micro-label text-gray-400 dark:text-gray-500 mb-1.5">Liabilities Change</p>
          <ChangeIndicator value={-changes.liabilities} />
        </div>
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm text-center">
          <p className="micro-label text-gray-400 dark:text-gray-500 mb-1.5">Net Worth Change</p>
          <ChangeIndicator value={changes.netWorth} />
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <GitCompare className="w-5 h-5 text-blue-500" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Period Comparison</h4>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={periods}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis fontSize={12} tickFormatter={(v) => fcShort(v)} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(val) => fc(val)}
                cursor={{ fill: 'rgba(79, 133, 255, 0.06)' }}
                contentStyle={{
                  backgroundColor: 'var(--tooltip-bg)',
                  color: 'var(--tooltip-text)',
                  border: '1px solid var(--tooltip-border)',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
                itemStyle={{ color: 'var(--tooltip-text)' }}
                labelStyle={{ color: 'var(--tooltip-text)' }}
              />
              <Legend />
              <Bar dataKey="assets" fill="#10B981" name="Assets" radius={[4, 4, 0, 0]} />
              <Bar dataKey="liabilities" fill="#EF4444" name="Liabilities" radius={[4, 4, 0, 0]} />
              <Bar dataKey="netWorth" fill="#4F85FF" name="Net Worth" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Account-by-Account Table */}
      <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Account Comparison</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800">
                <th className="text-left py-2 px-2 micro-label text-gray-400 dark:text-gray-500">Account</th>
                <th className="text-right py-2 px-2 micro-label text-gray-400 dark:text-gray-500">{previousPeriodLabel}</th>
                <th className="text-right py-2 px-2 micro-label text-gray-400 dark:text-gray-500">{currentPeriodLabel}</th>
                <th className="text-right py-2 px-2 micro-label text-gray-400 dark:text-gray-500">Change</th>
              </tr>
            </thead>
            <tbody>
              {[...(accounts.assets || []), ...(accounts.liabilities || [])].map(a => {
                const prev = Number(a.previousValue) || 0;
                const current = Number(a.currentValue) || 0;
                const change = current - prev;
                return (
                  <tr key={a.id} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50/70 dark:hover:bg-gray-800/30 transition-colors">
                    <td className="py-2 px-2 text-gray-900 dark:text-gray-100">{a.name}</td>
                    <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-num">{fc(prev)}</td>
                    <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100 font-medium font-num">{fc(current)}</td>
                    <td className="py-2 px-2 text-right"><ChangeIndicator value={change} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;
